import * as React from "react";
import { SlidersHorizontalIcon } from "lucide-react";

import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./dropdown-menu";

type DisplayOrderingOption = { value: string; label: React.ReactNode };

type DisplayProperty = {
  id: string;
  /** Title case column or property name. */
  label: React.ReactNode;
  visible: boolean;
  /** Shown but not toggleable, e.g. a column with no values yet. */
  disabled?: boolean;
};

type DisplayOptionsProps = {
  ordering?: {
    options: DisplayOrderingOption[];
    value: string;
    onValueChange: (value: string) => void;
  };
  properties?: DisplayProperty[];
  onPropertyChange?: (id: string, visible: boolean) => void;
  /** One sentence under the properties. */
  note?: React.ReactNode;
  align?: "start" | "end";
};

/**
 * Linear's display options: one ghost "Display" button per `ListPage` for
 * ordering and visible properties, kept apart from the filter chips.
 */
function DisplayOptions({
  ordering,
  properties = [],
  onPropertyChange,
  note,
  align = "end"
}: DisplayOptionsProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-slot="display-options"
          aria-label="Display Options"
        >
          <SlidersHorizontalIcon />
          <span className="hidden md:inline">Display</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-60">
        {ordering ? (
          <>
            <DropdownMenuLabel>Ordering</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={ordering.value}
              onValueChange={ordering.onValueChange}
            >
              {ordering.options.map((o) => (
                <DropdownMenuRadioItem key={o.value} value={o.value}>
                  {o.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        ) : null}
        {ordering && properties.length ? <DropdownMenuSeparator /> : null}
        {properties.length ? (
          <>
            <DropdownMenuLabel>Properties</DropdownMenuLabel>
            {properties.map((p) => (
              <DropdownMenuCheckboxItem
                key={p.id}
                checked={p.visible}
                disabled={p.disabled}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(on) => onPropertyChange?.(p.id, on === true)}
              >
                {p.label}
              </DropdownMenuCheckboxItem>
            ))}
          </>
        ) : null}
        {note ? (
          <p className="px-2 pt-1 pb-2 text-sm text-fg-3">{note}</p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { DisplayOptions };
export type { DisplayOptionsProps, DisplayOrderingOption, DisplayProperty };
